/**
 * Frontend client for the render-settings scope tree (server/modules/rendersettings).
 * Settings are stored per scope node — workspace `global`, per folder, per file —
 * and `getEffectiveSettings` returns them merged down the tree (most-specific wins).
 */
import { apiGet, apiPut } from "../shared/apiClient";

export type RenderScope = "global" | "folder" | "file";
export type RenderValues = Record<string, unknown>;

export interface RenderSettingsDoc {
  _id: string;
  workspaceId: string;
  scope: RenderScope;
  scopeId: string | null;
  values: RenderValues;
  createdAt: string;
  updatedAt: string;
}

/** Raw stored values for one scope node ({} if nothing saved there yet). */
export const getScopeSettings = (scope: RenderScope, scopeId?: string | null) => {
  const p = new URLSearchParams({ scope });
  if (scopeId) p.set("scopeId", scopeId);
  return apiGet<RenderValues>(`/render-settings?${p.toString()}`);
};

/** Upsert the values for one scope node. */
export const saveScopeSettings = (scope: RenderScope, scopeId: string | null, values: RenderValues) =>
  apiPut<RenderSettingsDoc>("/render-settings", { scope, scopeId, values });

/** Merged settings for a file, resolved down the global → folder → file tree. */
export const getEffectiveSettings = (fileId: string) =>
  apiGet<RenderValues>(`/render-settings/effective/${fileId}`);
